import { Box, Chip, Stack, Typography } from "@mui/material";
import {
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import dagre from "dagre";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useMagicOutputs } from "../hooks/use-magic.ts";
import type { MagicDefinition, MagicEvalOutput, MagicType } from "../types/magic.ts";
import type { TestRunStatus } from "../types.ts";

import "@xyflow/react/dist/style.css";

interface MagicNodeData {
  label: string;
  isHighlighted: boolean;
  isSelected: boolean;
  name: string;
  definition: MagicDefinition;
  [key: string]: unknown;
}

// Node type color mapping
const getTypeColors = (type: MagicType) => {
  switch (type) {
    case "trigger":
      return {
        border: "#10b981", // green-500
        background: "#d1fae5", // green-100
        chip: "#059669", // green-600
      };
    case "act":
      return {
        border: "#3b82f6", // blue-500
        background: "#dbeafe", // blue-100
        chip: "#2563eb", // blue-600
      };
    case "assert":
      return {
        border: "#f59e0b", // amber-500
        background: "#fef3c7", // amber-100
        chip: "#d97706", // amber-600
      };
    case "hook":
      return {
        border: "#8b5cf6", // violet-500
        background: "#ede9fe", // violet-100
        chip: "#7c3aed", // violet-600
      };
    default:
      return {
        border: "#6b7280", // gray-500
        background: "#f3f4f6", // gray-100
        chip: "#4b5563", // gray-600
      };
  }
};

const MagicNode = ({ data, isConnectable }: NodeProps) => {
  const { label, isHighlighted, isSelected, name, definition } = data as MagicNodeData;

  const typeColors = getTypeColors(definition.type);

  // Softer, less contrasting colors with special selected state
  let borderColor = typeColors.border;
  let backgroundColor = typeColors.background;
  let borderWidth = 2;

  if (isSelected) {
    borderColor = "#7c3aed"; // purple-600
    backgroundColor = "#ede9fe"; // purple-100
    borderWidth = 3;
  } else if (isHighlighted) {
    borderColor = "#a78bfa"; // purple-400
    backgroundColor = "#f5f3ff"; // purple-50
  }

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      border={borderWidth}
      bgcolor={backgroundColor}
      borderColor={borderColor}
      borderRadius={2}
      p={1}
      minWidth={180}
      sx={{
        cursor: "pointer",
        boxShadow: isSelected ? 3 : 0,
        "&:hover": {
          boxShadow: isSelected ? 4 : 2,
          transform: "translateY(-1px)",
          transition: "all 0.2s ease-in-out",
        },
      }}
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      <Stack direction="column" alignItems="center" spacing={0.5}>
        <Chip
          label={definition.type}
          size="small"
          sx={{
            height: 16,
            fontSize: 9,
            fontWeight: "bold",
            backgroundColor: typeColors.chip,
            color: "white",
            "& .MuiChip-label": {
              px: 0.75,
            },
          }}
        />
        <Typography
          variant="body1"
          fontWeight={isHighlighted ? "bold" : "medium"}
          align="center"
          fontSize={12}
          maxWidth={160}
          noWrap
        >
          {label}
        </Typography>
        {name !== label && (
          <Typography variant="caption" color="text.secondary" fontSize={9} maxWidth={160} noWrap>
            {name}
          </Typography>
        )}
        {definition.source === "code" && (
          <Chip
            label="code"
            size="small"
            variant="outlined"
            sx={{
              height: 14,
              fontSize: 8,
              "& .MuiChip-label": {
                px: 0.5,
              },
            }}
          />
        )}
      </Stack>
      <Handle type="source" position={Position.Right} isConnectable={isConnectable} />
    </Box>
  );
};

const nodeTypes = {
  magicDefinition: MagicNode,
};

interface MagicGraphVisualizationProps {
  definitions: MagicDefinition[];
  selectedDefinition: MagicDefinition | null;
  selectedEvalOutput?: MagicEvalOutput | null;
  onNodeClick: (definition: MagicDefinition) => void;
  onPaneClick?: () => void;
}

const MagicGraphContent: React.FC<MagicGraphVisualizationProps> = ({
  definitions,
  selectedDefinition,
  selectedEvalOutput,
  onNodeClick,
  onPaneClick,
}) => {
  const reactFlowInstance = useReactFlow();

  // Fetch runs for all unique definition names
  const uniqueNames = useMemo(
    () => Array.from(new Set(definitions.map((def) => def.name))),
    [definitions],
  );

  // Fetch all runs for all definition names
  const { data: allRuns } = useMagicOutputs(undefined, undefined, undefined, undefined);

  // Filter runs by the unique names we care about
  const relevantRuns = useMemo(() => {
    if (!allRuns) return [];
    return allRuns.filter((run) => uniqueNames.includes(run.name));
  }, [allRuns, uniqueNames]);

  // Get the latest run status for each definition name
  const latestRunsByName = useMemo(() => {
    const runMap = new Map<string, MagicEvalOutput>();

    // Group runs by name and keep the latest one (by created_at)
    relevantRuns.forEach((run) => {
      const existing = runMap.get(run.name);
      if (!existing || new Date(run.created_at) > new Date(existing.created_at)) {
        runMap.set(run.name, run);
      }
    });

    // Convert to status map
    const statusMap = new Map<string, TestRunStatus>();
    runMap.forEach((run, name) => {
      statusMap.set(name, run.output.status);
    });

    return statusMap;
  }, [relevantRuns]);

  // Calculate node positions using a simple layered layout
  const { nodes, edges } = useMemo(() => {
    if (!definitions || definitions.length === 0) {
      return { nodes: [], edges: [] };
    }

    // Build a dependency map (node -> its dependencies)
    const depMap = new Map<string, string[]>();
    definitions.forEach((def) => {
      depMap.set(def.x_id, def.dep_ids || []);
    });

    // Build reverse dependency map (node -> nodes that depend on it)
    const reverseDeps = new Map<string, string[]>();
    definitions.forEach((def) => {
      reverseDeps.set(def.x_id, []);
    });
    definitions.forEach((def) => {
      (def.dep_ids || []).forEach((depId) => {
        const dependents = reverseDeps.get(depId) || [];
        dependents.push(def.x_id);
        reverseDeps.set(depId, dependents);
      });
    });

    // Get all dependent nodes recursively
    const getAllDependents = (xId: string): Set<string> => {
      const result = new Set<string>();
      const visited = new Set<string>();

      const traverse = (id: string) => {
        if (visited.has(id)) return;
        visited.add(id);

        const directDependents = reverseDeps.get(id) || [];
        directDependents.forEach((depId) => {
          result.add(depId);
          traverse(depId);
        });
      };

      traverse(xId);
      return result;
    };

    // Get all dependent nodes if something is selected
    const dependentNodes = selectedDefinition
      ? getAllDependents(selectedDefinition.x_id)
      : new Set<string>();

    // Use Dagre for layout
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
      rankdir: "LR", // Left to right
      nodesep: 100, // Vertical spacing between nodes
      ranksep: 200, // Horizontal spacing between ranks
      marginx: 50,
      marginy: 50,
    });

    const nodeWidth = 200;
    const nodeHeight = 100;

    // Add nodes to dagre
    definitions.forEach((def) => {
      dagreGraph.setNode(def.x_id, { width: nodeWidth, height: nodeHeight });
    });

    // Add edges to dagre
    definitions.forEach((def) => {
      (def.dep_ids || []).forEach((depId) => {
        if (definitions.find((d) => d.x_id === depId)) {
          dagreGraph.setEdge(depId, def.x_id);
        }
      });
    });

    // Calculate layout
    dagre.layout(dagreGraph);

    // Get x_ids from eval output if present
    const evalXIds = selectedEvalOutput
      ? new Set(selectedEvalOutput.output.exec_info.map((info) => info.x_id))
      : null;

    // Create nodes with Dagre positions
    const flowNodes: Node[] = definitions.map((def) => {
      const nodeWithPosition = dagreGraph.node(def.x_id);

      // If eval output is provided, only select nodes in that specific eval run
      // Otherwise, select all nodes with the same name (entire subgraph)
      const isSelected = evalXIds ? evalXIds.has(def.x_id) : selectedDefinition?.name === def.name;

      const isHighlighted = dependentNodes.has(def.x_id);

      return {
        id: def.x_id,
        type: "magicDefinition",
        position: {
          x: nodeWithPosition.x - nodeWidth / 2,
          y: nodeWithPosition.y - nodeHeight / 2,
        },
        data: {
          label: def.x_id,
          isHighlighted,
          isSelected,
          name: def.name,
          definition: def,
        },
      };
    });

    // Create edges
    const flowEdges: Edge[] = [];
    definitions.forEach((def) => {
      (def.dep_ids || []).forEach((depId) => {
        // Check if the dependency exists in our definitions
        if (definitions.find((d) => d.x_id === depId)) {
          // Highlight edge if it's part of the dependency chain from selected node
          const isHighlighted = selectedDefinition
            ? (dependentNodes.has(def.x_id) || selectedDefinition.x_id === def.x_id) &&
              (dependentNodes.has(depId) || selectedDefinition.x_id === depId)
            : false;

          // Get the run status for this definition's name
          const runStatus = latestRunsByName.get(def.name);

          // Determine edge color based on run status
          let edgeColor = "#94a3b8"; // slate-400 (default/no run)
          let strokeWidth = 1.5;
          let strokeDasharray: string | undefined;

          if (runStatus === "passed") {
            edgeColor = "#22c55e"; // green-500
            strokeWidth = 2;
          } else if (runStatus === "failed") {
            edgeColor = "#ef4444"; // red-500
            strokeWidth = 2;
          } else if (runStatus === "running") {
            edgeColor = "#3b82f6"; // blue-500
            strokeWidth = 2;
          } else if (runStatus === "skipped") {
            edgeColor = "#f59e0b"; // amber-500
            strokeWidth = 2;
            strokeDasharray = "5 5"; // Dotted pattern
          }

          // Override with purple if highlighted by selection
          if (isHighlighted) {
            edgeColor = "#7c3aed"; // purple-600
            strokeWidth = 2.5;
            strokeDasharray = undefined; // Remove dotted pattern for highlighted edges
          }

          flowEdges.push({
            id: `${depId}-${def.x_id}`,
            source: depId,
            target: def.x_id,
            type: "default", // Use default bezier curves instead of smoothstep
            animated: isHighlighted || runStatus === "running",
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: edgeColor,
            },
            style: {
              stroke: edgeColor,
              strokeWidth,
              strokeDasharray,
            },
          });
        }
      });
    });

    return { nodes: flowNodes, edges: flowEdges };
  }, [definitions, selectedDefinition, selectedEvalOutput, latestRunsByName]);

  const onNodeClickHandler = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const definition = definitions.find((d) => d.x_id === node.id);
      if (definition) {
        onNodeClick(definition);
      }
    },
    [definitions, onNodeClick],
  );

  // Auto-fit view to selected subgraph or all nodes
  useEffect(() => {
    if (nodes.length === 0) return;

    if (selectedDefinition) {
      // Get all highlighted nodes (selected + dependents)
      const highlightedNodeIds = nodes
        .filter((node) => {
          const data = node.data as MagicNodeData;
          return data.isSelected || data.isHighlighted;
        })
        .map((node) => node.id);

      if (highlightedNodeIds.length > 0) {
        // Fit view to highlighted nodes with some padding
        setTimeout(() => {
          reactFlowInstance.fitView({
            padding: 0.2,
            includeHiddenNodes: false,
            nodes: highlightedNodeIds.map((id) => ({ id })),
            duration: 400,
          });
        }, 50);
      }
    } else {
      // No selection - fit to all nodes
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.3,
          duration: 400,
        });
      }, 50);
    }
  }, [selectedDefinition, nodes, reactFlowInstance]);

  return (
    <Box
      width="100%"
      height="100%"
      border={1}
      borderColor="divider"
      borderRadius={2}
      sx={{ backgroundColor: "background.default" }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodeClick={onNodeClickHandler}
        onPaneClick={onPaneClick}
        fitView
        fitViewOptions={{
          padding: 0.3,
          minZoom: 0.1,
          maxZoom: 2,
        }}
        minZoom={0.1}
        maxZoom={4}
        nodeTypes={nodeTypes}
        proOptions={{ hideAttribution: true }}
      >
        <Controls showZoom showFitView showInteractive />
      </ReactFlow>
    </Box>
  );
};

const MagicGraphVisualization: React.FC<MagicGraphVisualizationProps> = (props) => {
  return (
    <ReactFlowProvider>
      <MagicGraphContent {...props} />
    </ReactFlowProvider>
  );
};

export default MagicGraphVisualization;
